<script setup lang="ts">
import { ConfirmoOverlayMessageAction, Invoice } from '@confirmo/overlay'
import { ref, onBeforeUnmount, computed } from 'vue'
import { t } from '@/locales/i18n'
import { useStore } from 'vuex'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner.vue'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import { type Country } from '@/components/Payments/CountrySelect.vue'
import CountrySelect from '@/components/Payments/CountrySelect.vue'
import Logo from '@/assets/logo.svg'
import { usePaymentPlan } from '@/composables/paymentPlan'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import TierTag from '@/components/Sub/TierTag.vue'
import { updateSubscription, segmentTrackSuccess } from '@/helpers/paymentHelpers'
import {
  useCreateSubscription,
  useUpdateSubscription,
  useCreateOneTimePayment,
  useConfirmoCreditsPayment,
} from '@gto/api/vue-query'
import GwMenuNew from '@/components/lib/GwMenuNew/GwMenuNew.vue'
import GwDialogTemplate from '@/components/lib/GwDialogTemplate/GwDialogTemplate.vue'
import { logSentry } from '@gto/sentry'
import type { PaymentPlan } from '@/types/paymentPlans'

const {
  selectedPlan,
  selectedTierPlan,
  upgradingCurrentPlan,
  currentSubscription,
  currentTierPlan,
} = usePaymentPlan()
const store = useStore()

type CryptoGatewayProps =
  | {
      onSuccessPayment: (params: { payment_provider: string; payment_method: string }) => void
      amount: number
      isOneTimePayment: true
    }
  | {
      onSuccessPayment: (params: { payment_provider: string; payment_method: string }) => void
      isOneTimePayment: false
    }

const props = defineProps<{
  onSuccessPayment: (params: { payment_provider: string; payment_method: string }) => void
  amount?: number
  isOneTimePayment: boolean
  customPlan?: PaymentPlan
}>()

const emit = defineEmits(['payment-done'])

const overlay = ref<Invoice | null>(null)
const paymentState = ref<'initial' | 'loading' | 'confirming' | 'success'>('initial')
const invoiceUrl = ref<string | null>(null)
const selectedCountry = ref<Country | null>(null)
const updateSubscriptionTimedOut = ref(false)

const amount = computed(() =>
  props.isOneTimePayment ? props.amount : selectedPlan.value?.price || 0,
)

const displayError = async (error: string) => {
  paymentState.value = 'initial'
  store.dispatch('messages', {
    type: 'error',
    text: error,
    hide: true,
  })
}

const clearInvoice = () => {
  invoiceUrl.value = null
}

const handleUpdateSubscription = () => {
  const plan = selectedPlan.value
  if (!plan) {
    return
  }
  updateSubscriptionTimedOut.value = false
  updateSubscription(
    plan,
    'upgrade',
    () => {
      segmentTrackSuccess(selectedTierPlan.value, 'crypto')
      props.onSuccessPayment({
        payment_provider: 'confirmo',
        payment_method: 'crypto',
      })
    },
    () => {
      updateSubscriptionTimedOut.value = true
    },
  )
}

const confirmoMessageListener = (event: MessageEvent) => {
  if (
    !invoiceUrl.value ||
    !invoiceUrl.value.includes(event.origin) ||
    event.data.action !== ConfirmoOverlayMessageAction.CONFIRMO_OVERLAY_INVOICE_STATUS_CHANGE
  ) {
    return
  }
  switch (event.data.status) {
    case 'paid':
      paymentState.value = 'success'
      emit('payment-done')
      break
    case 'expired':
      displayError(t('payment.errors.payment_expired'))
      clearInvoice()
      paymentState.value = 'initial'
      break
    case 'error':
      displayError(t('payment.errors.payment_error'))
      clearInvoice()
      paymentState.value = 'initial'
      logSentry('Crypto Gateway: Payment failed', {
        error: JSON.stringify(event.data.error, null, 2),
        email: store.getters.user?.email,
        selectedPlan: selectedTierPlan.value?.full_name,
        currentPlan: currentTierPlan.value?.full_name,
      })
      break
    case 'confirming':
      paymentState.value = 'confirming'
      emit('payment-done')
      break
    default:
      break
  }
}

const handleClose = () => {
  if (paymentState.value === 'success') {
    if (!props.isOneTimePayment) {
      handleUpdateSubscription()
    }
    clearInvoice()
  }
  window.removeEventListener('message', confirmoMessageListener)
}
const { mutateAsync: mutateUpdate, data: updateData } = useUpdateSubscription()
const { mutateAsync: mutateCreate, data: createData } = useCreateSubscription()
const { mutateAsync: mutateCreditsPayment, data: creditsPaymentData } = useConfirmoCreditsPayment()
const { mutateAsync: mutateCreateOneTimePayment, data: createOneTimePaymentData } =
  useCreateOneTimePayment()

const fetchInvoice = async () => {
  if (!selectedCountry.value) {
    displayError(t('payment.errors.no_country_selected'))
    return null
  }
  if (props.customPlan) {
    await mutateCreditsPayment({
      data: {
        country_code: selectedCountry.value.code,
        credit_amount: props.customPlan!.credits,
      },
    })
    return creditsPaymentData.value?.url || null
  }

  if (props.isOneTimePayment) {
    await mutateCreateOneTimePayment({
      data: {
        country_code: selectedCountry.value.code,
        amount: props.amount!,
      },
    })
    return createOneTimePaymentData.value?.url || null
  }
  if (upgradingCurrentPlan.value) {
    await mutateUpdate({
      subscriptionId: currentSubscription.value?.public_id || '',
      data: {
        country_code: selectedCountry.value.code,
        plan_id: selectedPlan.value?.id!,
      },
    })
    return updateData.value?.url || null
  }
  await mutateCreate({
    data: {
      country_code: selectedCountry.value.code,
      plan_id: selectedPlan.value?.id!,
    },
  })
  return createData.value?.url || null
}

const openInvoiceOverlay = async () => {
  if (!props.isOneTimePayment && !selectedPlan.value?.id) {
    displayError(t('payment.errors.no_plan_selected'))
    return
  }
  if (!invoiceUrl.value) {
    paymentState.value = 'loading'
    invoiceUrl.value = await fetchInvoice()
    if (!invoiceUrl.value) {
      displayError(t('payment.errors.create_payment'))
      logSentry('Crypto Gateway: Payment creation failed', {
        error: t('payment.errors.create_payment'),
        email: store.getters.user?.email,
        selectedPlan: selectedTierPlan.value?.full_name,
        currentPlan: currentTierPlan.value?.full_name,
      })
      return
    }
    paymentState.value = 'initial'
  }
  overlay.value = Invoice.open(invoiceUrl.value!, handleClose, {
    closeAfterPaid: true,
    closeAfterPaidTimeoutMs: props.customPlan ? 0 : 3000,
  })
  window.addEventListener('message', confirmoMessageListener)
}

const handleCountryUpdate = (country: Country) => {
  selectedCountry.value = country
  clearInvoice()
}

const hasPrivateRelayEmail = computed(() => {
  const { email } = store.getters.user_info
  return email.includes('privaterelay.appleid.com')
})

onBeforeUnmount(() => {
  handleClose()
})
</script>
<template>
  <div class="w-100 mt-4 d-flex flex-column align-center">
    <div
      v-if="isOneTimePayment"
      class="d-flex flex-column align-center ga-2 mt-4 mb-8"
    >
      <div
        class="d-flex flex-row align-center ga-1"
        style="transform: translateY(1.5rem)"
      >
        <Logo style="fill: var(--clr-primary); height: 4.5rem" />
        <i
          class="mdi mdi-bitcoin"
          style="font-size: 6rem; height: 8.75rem; color: var(--clr-yellow)"
        ></i>
      </div>
      <GwText variant="text_white_largest_700">
        {{ $t('payment.crypto.one_time_crypto_payment') }}
      </GwText>
      <GwText variant="text_white_normal_500">
        {{ store.getters.user_info.email }}
      </GwText>
      <GwText
        variant="text_white_largest_700"
        class="clr-primary"
      >
        {{ amount }} USD
      </GwText>
    </div>
    <div
      v-if="paymentState === 'initial'"
      class="d-flex flex-column align-center ga-4"
    >
      <CountrySelect
        :selectedCountry
        @update:country="handleCountryUpdate"
      />
      <div
        v-if="isOneTimePayment"
        class="mt-1"
        style="border-radius: 0.5rem"
        :class="selectedCountry ? 'anim-slide-shine' : ''"
      >
        <GwTooltipNew
          :tooltip="t('payment.crypto.please_select_country')"
          placement="contextmenu"
        >
          <template #opener="{ listeners }">
            <div v-on="!selectedCountry && listeners">
              <GwBtn
                class="checkout-btn"
                :variant="isOneTimePayment ? 'btn_primary-v2-large' : 'btn_primary'"
                :disabled="!selectedCountry"
                @click="openInvoiceOverlay"
              >
                {{ t('btn.continue') }}
              </GwBtn>
            </div>
          </template>
        </GwTooltipNew>
      </div>
      <GwMenuNew
        v-else
        hasBackOverlayBlur
        boxVariant="box_dialog"
        transitionVariant="dialog"
        :offset="{ top: -25 }"
      >
        <template #opener="{ listeners: menuListeners }">
          <GwTooltipNew
            :tooltip="t('payment.crypto.please_select_country')"
            placement="contextmenu"
          >
            <template #opener="{ listeners }">
              <div v-on="!selectedCountry && listeners">
                <div
                  class="mt-1"
                  style="border-radius: 0.5rem"
                  :class="selectedCountry ? 'anim-slide-shine' : ''"
                >
                  <GwBtn
                    class="checkout-btn"
                    :variant="isOneTimePayment ? 'btn_primary-v2-large' : 'btn_primary'"
                    :disabled="!selectedCountry"
                    v-on="menuListeners"
                  >
                    {{ t('btn.continue') }}
                  </GwBtn>
                </div>
              </div>
            </template>
          </GwTooltipNew>
        </template>
        <template #content="{ methods: { close } }">
          <GwDialogTemplate :modalTitle="t('payment.crypto.no_refund.title')">
            <template #content>
              <div class="d-flex flex-column align-center ga-2 py-4">
                <GwText
                  variant="text_white_normal-large_500"
                  class="text-wrap text-center"
                  style="max-width: 36rem"
                  v-html="t('payment.crypto.no_refund.text')"
                />
              </div>
            </template>
            <template #actions>
              <div class="d-flex flex-row ga-2 w-100 justify-space-between">
                <GwBtn
                  variant="btn_text_grey_white"
                  @click="close"
                >
                  {{ t('btn.cancel') }}
                </GwBtn>
                <GwBtn
                  variant="btn_primary"
                  @click="openInvoiceOverlay"
                >
                  {{ t('btn.continue') }}
                </GwBtn>
              </div>
            </template>
          </GwDialogTemplate>
        </template>
      </GwMenuNew>
    </div>
    <div
      v-else-if="paymentState === 'loading'"
      class="d-flex flex-column align-center mb-6"
    >
      <LoaderSpinner
        class="ma-auto"
        style="--size: 8rem"
      />
      <GwText
        class="text-center text-wrap"
        variant="text_white_large_400"
      >
        {{ $t(`payment.crypto.state.loading`) }}
      </GwText>
    </div>
    <div
      v-else-if="paymentState === 'success'"
      class="d-flex flex-column align-center mb-6"
    >
      <GwText
        variant="text_white_largest_700"
        class="clr-primary"
      >
        {{ $t('payment.crypto.state.success.title') }}
      </GwText>
      <GwText
        class="text-center text-wrap"
        variant="text_white_large_400"
      >
        {{ $t('payment.crypto.state.success.subtitle') }}
      </GwText>
      <div
        v-if="!isOneTimePayment"
        class="mt-8"
      >
        <div
          v-if="!updateSubscriptionTimedOut"
          class="d-flex flex-column align-center ga-1"
        >
          <LoaderSpinner
            class="ma-auto"
            style="--size: 8rem"
          />
          <GwText variant="text_white_large_400">
            {{ $t('payment.updating_subscription') }}
          </GwText>
          <TierTag
            v-if="selectedPlan"
            class="f-center mt-2"
            data-tst="payment_upgrade_tier"
            :customGame="selectedPlan.type"
            :customTier="selectedPlan.tier"
            :paymentInterval="selectedPlan.interval"
          />
        </div>
        <div
          v-else
          class="d-flex flex-column align-center ga-1"
        >
          <GwText
            class="text-center w-100"
            variant="text_white_normal-large_500"
            v-html="
              $t('payment.crypto.state.success.update_subcription', {
                email: store.getters.user_info.email,
              })
            "
          />
          <GwText
            v-if="hasPrivateRelayEmail"
            class="text-center text-wrap mt-2"
            variant="text_white_normal_700"
            style="--text-size: 1.25rem"
            >{{ $t('payment.crypto.private_relay') }}
          </GwText>
        </div>
      </div>
    </div>
    <div
      v-else-if="paymentState === 'confirming'"
      class="d-flex flex-column align-center mb-6 ga-2"
    >
      <GwText variant="text_white_largest_700">
        {{ $t('payment.crypto.state.confirming.title') }}
      </GwText>
      <GwText
        variant="text_white_normal-large_500"
        class="text-center text-wrap"
        style="max-width: 36rem"
        v-html="
          $t('payment.crypto.state.confirming.update_subcription', {
            email: store.getters.user_info.email,
          })
        "
      />
      <!-- TODO: Uncomment when emails are implemented on the BE
      <GwTextx2
        v-if="hasPrivateRelayEmail"
        class="text-center text-wrap mt-2"
        variant="text_white_normal_700"
        style="--text-size: 1.25rem"
        >{{ $t('payment.crypto.private_relay') }}
      </GwText> -->
    </div>
    <div
      v-if="paymentState === 'success' || paymentState !== 'confirming'"
      class="d-flex flex-column mt-8"
    >
      <div class="d-flex flex-row mx-auto ga-2">
        <i
          class="mdi mdi-lock-outline"
          style="font-size: 1.25rem"
        ></i>
        <GwText
          variant="text_white_normal-large_500"
          class="w-100"
          style="max-width: 28rem; --text-size: 1.25rem"
        >
          {{ $t('payment.safe_secure_checkout') }}
        </GwText>
      </div>
      <GwText
        variant="text_grey_normal_400"
        class="text-center mx-auto mt-1"
        style="max-width: 24rem; font-size: 12px"
      >
        {{ $t('payment.crypto.payment_protection') }}
      </GwText>
    </div>
    <div
      v-else
      class="d-flex f-row mt-4"
    >
      <GwText variant="text_grey_normal_500">
        {{ $t('payment.crypto.help_start') }}
      </GwText>
      <OpenChatLink class="mx-1" />
      <GwText
        variant="text_grey_normal_500"
        v-html="$t('payment.crypto.help_end', { email: 'hello@gtowizard.com' })"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.checkout-btn {
  color: white;
  text-transform: uppercase;
  margin: 0 auto;
  justify-content: center;
  padding: 1.5rem 3rem;
  font-size: 1rem;
  width: 24rem;
  height: 44px;
  --text-weight: 500;
  letter-spacing: 1.2px;
  --surface-ct-bg: var(--clr-primary);
  &[data-state='disabled'] {
    --surface-ct-opacity: 0.5;
    --surface-ct-bg: var(--clr-grey-2);
    --text-clr: var(--clr-black-1);
  }
}
</style>
